import React, { useState, useEffect, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import "./SoundSeeker.scss";
import { sleep, shuffle } from "../shared/util";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import useTimer from "../shared/useTimer";
import Word from "../shared/Word";
import Container from "../shared/Container";
import { ImageOption } from "../shared/Image";
import { getSoundSeekerImages, getWordFromUrl } from "./imageUtil";

import icon from "./soundseeker-icon.png";
import {getUserData} from "Services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function SoundSeekerValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.sound_seeker;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  // must have at least one of: startImage, endImage
  const images = getSoundSeekerImages(props.wordMetadata.PHON, getUserData().locale);
  const { imageStart, imageEnd } = images;
  const isValid = !!imageStart || !!imageEnd;

  useEffect(() => {
    if (!isValid) close();
  }, []);

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: SoundSeeker1,
  }[versionId];

  return <Version {...props} close={close} versionId={versionId} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function SoundSeeker1(props) {
  const { word, wordMetadata, speech, wcpm } = props;
  const { speak, speakFile, speakSequence } = useConversation(speech);
  const { cancellablePromise } = useCancellablePromise();

  const { PHON } = wordMetadata;
  const content = getSoundSeekerImages(PHON, getUserData().locale);

  const position = useMemo(() => {
    const { imageStart, imageEnd } = content;

    let pos;
    if(imageStart && imageEnd) {
      // when both positions have valid content, use wcpm-based probability to choose one
      // use get cutoff based on wcpm
      let cutoff = 100;
      if (wcpm > 10) cutoff = 85;
      if (wcpm > 20) cutoff = 60;
      if (wcpm > 30) cutoff = 40;
      if (wcpm > 60) cutoff = 20;

      // determine position
      const i = Math.floor(Math.random() * 100);
      pos = (i < cutoff) ? "start" : "end";
    } else {
      //choose the only one position with valid content
      pos = imageStart ? "start" : "end";
    }

    return pos;
  }, []);

  const isStart = position === "start";
  const files = getAudioForPhonemes(PHON, getUserData().locale);
  const soundFile = isStart ? files[0] : files[files.length - 1];

  const [stage, setStage] = useState(0);
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [highlightedUrl, setHighlightedUrl] = useState(null);
  const { startTimer, restartTimer, clearTimer } = useTimer({
    timeout: 10000,
    onEnd: giveCorrectAnswer,
  });
  const multipleChoiceData = useMemo(generateChoices, []);

  function generateChoices() {
    const { imageStart, imageEnd, distractorsStart, distractorsEnd } = content;
    const target = isStart ? imageStart : imageEnd;
    const distractors = isStart ? distractorsStart : distractorsEnd;
    const options = shuffle([...distractors, target]);
    return { options, target };
  }

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.intro,
              {textToSpeak: word, skipTranslation: true},
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.intro} ${word}.`));
          }
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.describe_1({ isStart }),
            ]));
          } else {
            await cancellablePromise(speak(`${word} ${ttsStrings.describe_1({ isStart })}.`));
          }
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(soundFile));
          await cancellablePromise(speak(ttsStrings.ask_word({ isStart })));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(soundFile));
          setStage(2);
          break;
        case 2:
          startTimer();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return <Container icon={icon}>{renderContent()}</Container>;

  function renderContent() {
    if (stage === 0) return <Word>{word}</Word>;

    if (stage === 1) {
      const { options } = multipleChoiceData;

      return (
        <div className="soundseeker-images">
          {options.map((url, i) => (
            <ImageOption key={i} src={url} disabled={true} />
          ))}
        </div>
      );
    }

    if (stage === 2) {
      const { options, target } = multipleChoiceData;

      return (
        <div className="soundseeker-images">
          {options.map((url, i) => {
            const isTarget = url === target;
            const isHighlighted = url === highlightedUrl;
            const isSelected = revealAnswer && isTarget;
            let isDisabled = false;
            if (revealAnswer && !isTarget) isDisabled = true;
            if (highlightedUrl && !isHighlighted) isDisabled = true;
            return (
              <ImageOption
                key={i}
                src={url}
                highlighted={isHighlighted}
                selected={isSelected}
                disabled={isDisabled}
                onClick={async () => {
                  try {
                    if (url === target) {
                      // correct
                      setRevealAnswer(true);
                      clearTimer();
                      if(getUserData().bilingualMode) {
                        await cancellablePromise(speakSequence([
                          ttsStrings.yes,
                          {textToSpeak: getWordFromUrl(url), skipTranslation: true},
                          ttsStrings.describe_2({ isStart }),
                        ]));
                      } else {
                        await cancellablePromise(speak(`${ttsStrings.yes}, ${getWordFromUrl(url)} ${ttsStrings.describe_2({ isStart })}.`));
                      }
                      await cancellablePromise(sleep(500));
                      await cancellablePromise(speakFile(soundFile));
                      await cancellablePromise(speak(ttsStrings.correct));
                      props.close();
                    } else {
                      // incorrect
                      if (attempts === 0) {
                        restartTimer();
                        setHighlightedUrl(url);
                        if(getUserData().bilingualMode) {
                          await cancellablePromise(speakSequence([
                            ttsStrings.no,
                            {textToSpeak: getWordFromUrl(url), skipTranslation: true},
                            ttsStrings.incorrect({ isStart }),
                          ]));
                        } else {
                          await cancellablePromise(speak(`${ttsStrings.no}, ${getWordFromUrl(url)} ${ttsStrings.incorrect({ isStart })}.`));
                        }
                        await cancellablePromise(sleep(500));
                        await cancellablePromise(speakFile(soundFile));
                        setHighlightedUrl(null);
                        setAttempts(1);
                      } else if (attempts === 1) {
                        clearTimer();
                        setHighlightedUrl(url);
                        await cancellablePromise(sleep());
                        setHighlightedUrl(null);
                        giveCorrectAnswer();
                      }
                    }
                  } catch (error) {
                    if (error.isCancelled) return;
                    throw(error);
                  }
                }}
              />
            );
          })}
        </div>
      );
    }
  }

  async function giveCorrectAnswer() {
    if (!props.isMountedRef.current) return;

    try {
      const { target } = multipleChoiceData;

      clearTimer();
      setRevealAnswer(true);

      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.its_this_one,
          {textToSpeak: getWordFromUrl(target), skipTranslation: true},
          ttsStrings.describe_1({ isStart }),
        ]));
      } else {
        await cancellablePromise(speak(`${ttsStrings.its_this_one} ${getWordFromUrl(target)} ${ttsStrings.describe_1({ isStart })}`));
      }
      await cancellablePromise(sleep(500));
      await cancellablePromise(speakFile(soundFile));
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.like_word,
          {textToSpeak: word, skipTranslation: true},
        ]));
      } else {
        await cancellablePromise(speak(`${ttsStrings.like_word} ${word}.`));
      }
      props.close();
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}
